/**
 * apiFetch — auth-aware POST helper.
 *
 * Replaces the legacy Axios(endpoint, body, callback) callback pattern with a
 * simple async function that returns data directly.
 *
 * Usage (replaces callback-style):
 *   // Before:
 *   Axios('/api/v2/search/hcp', body, (data) => setState({ data }))
 *
 *   // After:
 *   apiFetch('/api/v2/search/hcp', body).then(data => setState({ data }))
 *   // or in async context:
 *   const data = await apiFetch('/api/v2/search/hcp', body)
 *
 * All v2 endpoints return the data directly (no envelope wrapper).
 * v1 Workflow endpoints stay on the legacy Axios wrapper.
 */

import Swal from 'sweetalert2';
import authService from './api-authorization/AuthorizeService';
import { ApplicationPaths } from './api-authorization/ApiAuthorizationConstants';

/**
 * @param {string} endpoint  — API path, e.g. '/api/v2/search/hcp'
 * @param {object} body      — JSON-serializable request body
 * @returns {Promise<any>}   — parsed JSON response body
 * @throws  on HTTP errors (non-2xx), shows Session Expired dialog on 401
 */
export default async function apiFetch(endpoint, body) {
    const token = await authService.getAccessToken();
    const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
    });

    if (response.status === 401) {
        // Force a silent token refresh in case the server restarted (dev signing key rotated)
        // or the token silently expired between fetch calls.
        authService._token = null;
        authService._tokenExpiresAt = null;
        const refreshedToken = await authService.getAccessToken();
        if (refreshedToken) {
            // Retry the original request once with the new token.
            const retryResponse = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${refreshedToken}` },
                body: JSON.stringify(body),
            });
            if (retryResponse.ok) {
                const retryText = await retryResponse.text();
                return retryText ? JSON.parse(retryText) : null;
            }
            if (retryResponse.status !== 401) {
                throw new Error(`HTTP ${retryResponse.status} from ${endpoint}`);
            }
        }
        // Token refresh failed or retry still 401 — session is truly gone.
        Swal.fire({
            title: 'Session Expired',
            text: 'Your session has expired. Please login again.',
            showCancelButton: false,
            confirmButtonColor: '#5052bb',
            confirmButtonText: 'Yes',
        }).then(() => {
            window.location.href = ApplicationPaths.LogOut;
        });
        throw new Error('401 Unauthorized');
    }

    if (!response.ok) {
        throw new Error(`HTTP ${response.status} from ${endpoint}`);
    }

    const text = await response.text();
    return text ? JSON.parse(text) : null;
}
